export const analyzeECGImage = async (imageFile) => {
  return new Promise((resolve, reject) => {
    if (!imageFile.type.startsWith('image/')) {
      return reject('Por favor, envie um arquivo de imagem válido.');
    }

    const img = new Image();
    const imageURL = URL.createObjectURL(imageFile);
    img.src = imageURL;

    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');

        // Desenha a imagem no canvas
        ctx.drawImage(img, 0, 0);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;

        // Processa a imagem e detecta padrões
        const { peaksDetected, noiseLevel, detectedPatterns } =
          processECGPatterns(data, canvas.width, canvas.height);

        const summary = generateECGSummary(
          peaksDetected,
          noiseLevel,
          detectedPatterns
        );

        URL.revokeObjectURL(imageURL);
        resolve({ peaksDetected, noiseLevel, detectedPatterns, summary });
      } catch (error) {
        reject(`Erro ao processar a imagem: ${error.message}`);
      }
    };

    img.onerror = () => {
      reject('Erro ao carregar a imagem.');
      URL.revokeObjectURL(imageURL);
    };
  });
};

// Processa a imagem em blocos menores para capturar mais padrões
const processECGPatterns = (imageData, width, height) => {
  let peakCount = 0;
  let totalNoise = 0;
  const blockSize = 8; // Blocos menores para capturar mais detalhes
  const detectedPatterns = new Set(); // Utilizar Set para evitar duplicação

  // Percorre a imagem em blocos para otimização
  for (let y = 0; y < height; y += blockSize) {
    for (let x = 0; x < width; x += blockSize) {
      let blockPeakCount = 0;
      let blockNoise = 0;

      for (
        let i = y * width + x;
        i < (y + blockSize) * width + x + blockSize;
        i += 4
      ) {
        const r = imageData[i];
        const g = imageData[i + 1];
        const b = imageData[i + 2];

        if (r < 50 && g < 50 && b < 50) {
          blockPeakCount++;
        }

        if (i >= 4) {
          const prevPixel = imageData[i - 4];
          const diff = Math.abs(prevPixel - r);
          blockNoise += diff;
        }
      }

      peakCount += blockPeakCount;
      totalNoise += blockNoise;

      // Detecta padrões específicos de ECG e adiciona ao Set
      const blockPatterns = detectSpecificECGPatterns(
        blockPeakCount,
        blockNoise
      );
      blockPatterns.forEach((pattern) => detectedPatterns.add(pattern));
    }
  }

  const normalizedPeaks = (peakCount / (width * height)) * 10000;
  const noiseLevel = totalNoise / (width * height);

  return {
    peaksDetected: Math.floor(normalizedPeaks),
    noiseLevel: noiseLevel.toFixed(2),
    detectedPatterns: Array.from(detectedPatterns), // Converter Set para Array
  };
};

// Função para detectar padrões específicos de ECG com base na literatura médica
const detectSpecificECGPatterns = (blockPeakCount, blockNoise) => {
  const patterns = [];

  // Implementação de padrões específicos
  if (blockPeakCount < 50 && blockNoise < 5) {
    patterns.push('Bradicardia Sinusal');
  } else if (blockPeakCount > 150 && blockNoise < 10) {
    patterns.push('Taquicardia Sinusal');
  }

  if (blockPeakCount > 100 && blockNoise > 10) {
    patterns.push('Fibrilação Atrial');
  }

  if (blockPeakCount > 200 && blockNoise > 20) {
    patterns.push('Taquicardia Ventricular');
  }

  if (blockNoise > 30) {
    patterns.push('ECG com Ruído Elevado');
  }

  // Padrões adicionais
  if (blockPeakCount < 100 && blockNoise < 10) {
    patterns.push('Isquemia Miocárdica');
  }

  if (blockPeakCount > 150 && blockNoise > 15) {
    patterns.push('Infarto Agudo do Miocárdio');
  }

  return patterns;
};

// Função para fornecer a descrição dos padrões detectados
const getPatternDescription = (pattern) => {
  const descriptions = {
    'Bradicardia Sinusal':
      'Frequência cardíaca baixa, geralmente abaixo de 60 batimentos por minuto.',
    'Taquicardia Sinusal':
      'Frequência cardíaca rápida, geralmente acima de 100 batimentos por minuto.',
    'Fibrilação Atrial':
      'Batimento irregular e rápido, onde as câmaras superiores do coração (átrios) batem de forma caótica.',
    'Taquicardia Ventricular':
      'Batimento rápido que começa nas câmaras inferiores do coração (ventrículos).',
    'ECG com Ruído Elevado':
      'A análise do ECG apresentou interferência elevada, comprometendo a precisão dos resultados. Recomendado revisar a qualidade do sinal.',
    'Isquemia Miocárdica':
      'Redução do fluxo sanguíneo para o músculo cardíaco, geralmente causado por estreitamento das artérias coronárias.',
    'Infarto Agudo do Miocárdio':
      'Bloqueio do fluxo sanguíneo para o coração, resultando em danos ao músculo cardíaco.',
  };

  return descriptions[pattern] || 'Descrição não disponível';
};

// Gera um resumo detalhado com base nos padrões detectados e adiciona descrição
const generateECGSummary = (peaksDetected, noiseLevel, detectedPatterns) => {
  let summary = `Picos detectados: ${peaksDetected}. Nível de ruído: ${noiseLevel}. \n`;

  if (detectedPatterns.length > 0) {
    summary += `Padrões detectados: \n`;
    detectedPatterns.forEach((pattern) => {
      summary += `- ${pattern}: ${getPatternDescription(pattern)}\n`;
    });
  } else {
    summary += 'Nenhum padrão específico de ECG detectado.\n';
  }

  if (peaksDetected < 500 && noiseLevel < 10) {
    summary += 'O ECG mostra padrões normais. Nenhum problema detectado.';
  } else if (peaksDetected >= 500 && peaksDetected < 1500) {
    summary += 'Padrões irregulares detectados. Revisar com um especialista.';
  } else {
    summary +=
      'Anomalias críticas detectadas. Consulta médica urgente necessária.';
  }

  if (noiseLevel >= 10) {
    summary +=
      ' Nível de ruído elevado. A análise pode não ser totalmente precisa.';
  }

  return summary;
};
